/**
 * EVJ Smart Cart recommendations.
 * Priority: same name word → complementary → related → best sellers.
 * Multi-variant products (size / color) prompt before add.
 * Does not touch GTM / AW / Klaviyo / theme.min.js.
 */
(function () {
  var LIST_SEL = "[data-evj-smart-cart-list]";
  var ROOT_SEL = "[data-evj-smart-cart]";
  var RV_KEY = "evjRecentlyViewed";
  var RV_MAX = 10;
  var productById = {};

  function money(centsVal) {
    var n = Number(centsVal) / 100;
    if (isNaN(n)) return "";
    return "$" + n.toFixed(2);
  }

  function cents(price) {
    if (typeof price === "string" && price.indexOf(".") !== -1) {
      return Math.round(parseFloat(price) * 100);
    }
    return Number(price) || 0;
  }

  function cartIdsFromRoot(root) {
    return String(root.getAttribute("data-cart-product-ids") || "")
      .split(",")
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
  }

  function fetchCart() {
    return fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) {
        return r.ok ? r.json() : { items: [] };
      })
      .then(function (cart) {
        var ids = [];
        var titles = [];
        (cart.items || []).forEach(function (item) {
          ids.push(String(item.product_id));
          if (item.product_title) titles.push(item.product_title);
        });
        return { ids: ids, titles: titles };
      })
      .catch(function () {
        return { ids: [], titles: [] };
      });
  }

  var NAME_STOP = {
    ellie: 1,
    vail: 1,
    jewelry: 1,
    the: 1,
    and: 1,
    with: 1,
    for: 1,
    gold: 1,
    silver: 1,
    gld: 1,
    slvr: 1,
    vermeil: 1,
    plated: 1,
    necklace: 1,
    necklaces: 1,
    nklc: 1,
    bracelet: 1,
    bracelets: 1,
    brct: 1,
    earring: 1,
    earrings: 1,
    ear: 1,
    ring: 1,
    rings: 1,
    rng: 1,
    anklet: 1,
    anklets: 1,
    hoop: 1,
    hoops: 1,
    stud: 1,
    studs: 1,
    huggie: 1,
    huggies: 1,
    set: 1,
    mini: 1,
    large: 1,
    small: 1,
    medium: 1,
    water: 1,
    resistant: 1,
    waterproof: 1,
    hypoallergenic: 1,
    oversized: 1,
    chain: 1,
    link: 1,
    tube: 1,
    cuff: 1,
    tennis: 1,
    paperclip: 1,
    classic: 1,
    dainty: 1,
  };

  function nameWords(title) {
    return String(title || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, " ")
      .split(/[\s-]+/)
      .filter(function (w) {
        return w.length >= 3 && !NAME_STOP[w] && !/^\d+$/.test(w);
      });
  }

  function wordGroups(titles) {
    var primary = [];
    var extra = [];
    var seenW = {};
    (titles || []).forEach(function (t) {
      var ws = nameWords(t);
      ws.forEach(function (w, i) {
        if (seenW[w]) return;
        seenW[w] = true;
        if (i === 0) primary.push(w);
        else extra.push(w);
      });
    });
    return {
      primary: primary.slice(0, 3),
      extra: extra.slice(0, 3),
      all: primary.concat(extra).slice(0, 4),
    };
  }

  function collectionWords(titles) {
    return wordGroups(titles).all;
  }

  function titleHasAnyWord(title, words) {
    var t = String(title || "");
    return (words || []).some(function (w) {
      return new RegExp("(^|[^a-z0-9])" + w + "([^a-z0-9]|$)", "i").test(t);
    });
  }

  function firstAvailableVariant(product) {
    var vs = product.variants || [];
    for (var i = 0; i < vs.length; i++) {
      if (vs[i].available !== false) return vs[i];
    }
    return vs[0] || null;
  }

  function optionNames(product) {
    var raw = product.options || [];
    return raw
      .map(function (o) {
        return typeof o === "string" ? o : o && o.name;
      })
      .filter(function (n) {
        return n && String(n).toLowerCase() !== "title";
      });
  }

  function optionValues(product, index) {
    var seen = {};
    var out = [];
    (product.variants || []).forEach(function (v) {
      var val = v["option" + index];
      if (!val || val === "Default Title" || seen[val]) return;
      seen[val] = true;
      out.push(val);
    });
    return out;
  }

  function pickerOptions(product) {
    var names = optionNames(product);
    if (!names.length && (product.variants || []).length > 1) names = ["Option"];
    return names
      .map(function (name, i) {
        return { name: name, index: i + 1, values: optionValues(product, i + 1) };
      })
      .filter(function (o) {
        return o.values.length > 0;
      });
  }

  function needsChoice(product) {
    var vs = (product.variants || []).filter(function (v) {
      return v && v.id;
    });
    if (vs.length <= 1) return false;
    return pickerOptions(product).length > 0;
  }

  function findVariant(product, selected) {
    var vs = product.variants || [];
    for (var i = 0; i < vs.length; i++) {
      var v = vs[i];
      var ok = true;
      for (var n = 1; n <= 3; n++) {
        if (selected[n] && v["option" + n] !== selected[n]) ok = false;
      }
      if (ok) return v;
    }
    return null;
  }

  function normalize(p, source) {
    if (!p) return null;
    var variants = (p.variants || []).map(function (v) {
      return {
        id: v.id || v.variant_id,
        title: v.title,
        option1: v.option1,
        option2: v.option2,
        option3: v.option3,
        available: v.available !== false,
        price: cents(v.price),
      };
    });
    if (!variants.length && p.variant_id) {
      variants = [
        {
          id: p.variant_id,
          available: p.available !== false,
          price: cents(p.price),
        },
      ];
    }
    var v = firstAvailableVariant({ variants: variants });
    var img = p.featured_image || p.image || "";
    if (img && typeof img === "object") img = img.src || img.url || "";
    if (!img && p.images && p.images[0]) {
      img = typeof p.images[0] === "string" ? p.images[0] : p.images[0].src || p.images[0].url;
    }
    var available = p.available !== false && !!(v && v.available !== false && v.id);
    return {
      id: p.id,
      handle: p.handle,
      title: p.title,
      available: available,
      featured_image: img,
      source: source || p.source || "rec",
      options: p.options || [],
      variants: variants,
    };
  }

  function hydrateProduct(p, source) {
    var handle = (p && p.handle) || "";
    var fallback = normalize(p, source);
    if (!handle) return Promise.resolve(fallback);
    return fetch("/products/" + encodeURIComponent(handle) + ".js", {
      credentials: "same-origin",
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (full) {
        var n = full ? normalize(full, source || (p && p.source)) : fallback;
        if (n && p && p.source) n.source = p.source;
        return n;
      })
      .catch(function () {
        return fallback;
      });
  }

  function fetchSameName(titles) {
    var words = collectionWords(titles);
    if (!words.length) return Promise.resolve([]);
    return Promise.all(
      words.map(function (w) {
        return fetch(
          "/search/suggest.json?q=" +
            encodeURIComponent(w) +
            "&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=hide",
          { credentials: "same-origin" }
        )
          .then(function (r) {
            return r.ok ? r.json() : {};
          })
          .then(function (data) {
            return (
              (data.resources &&
                data.resources.results &&
                data.resources.results.products) ||
              []
            ).filter(function (p) {
              return titleHasAnyWord(p.title, words);
            });
          })
          .catch(function () {
            return [];
          });
      })
    ).then(function (chunks) {
      var raw = [];
      var seen = {};
      chunks.forEach(function (list) {
        (list || []).forEach(function (p) {
          var key = String((p && (p.id || p.handle)) || "");
          if (!key || seen[key]) return;
          seen[key] = true;
          raw.push(p);
        });
      });
      return Promise.all(
        raw.map(function (p) {
          return hydrateProduct(p, "set");
        })
      );
    });
  }

  function readRV() {
    try {
      var raw = localStorage.getItem(RV_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function writeRV(list) {
    try {
      localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, RV_MAX)));
    } catch (e) {}
  }

  function recordViewed(item) {
    if (!item || !item.id) return;
    var list = readRV().filter(function (p) {
      return String(p.id) !== String(item.id);
    });
    list.unshift(item);
    writeRV(list);
  }

  function productCard(product) {
    var v = firstAvailableVariant(product);
    if (!v || !product.available || !v.id) return "";
    if (product.id) productById[String(product.id)] = product;
    var img = product.featured_image || "";
    if (img && img.indexOf("?") === -1) img += "?width=240";
    else if (img) img += "&width=240";
    var href = "/products/" + product.handle;
    var title = (product.title || "").replace(/^Ellie Vail\s*[-–]?\s*/i, "");
    var why =
      product.source === "set"
        ? "Same set"
        : product.source === "popular"
          ? "Popular"
          : "Pairs well";
    var choose = needsChoice(product);
    var btn = choose
      ? '<button type="button" class="evj-smart-cart__add btn btn--tertiary btn--x-small" data-evj-choose="' +
        product.id +
        '">Add</button>'
      : '<button type="button" class="evj-smart-cart__add btn btn--tertiary btn--x-small" data-evj-add="' +
        v.id +
        '">Add</button>';
    return (
      '<article class="evj-smart-cart__item" data-evj-product-id="' +
      product.id +
      '">' +
      '<a class="evj-smart-cart__img-wrap" href="' +
      href +
      '">' +
      (img
        ? '<img class="evj-smart-cart__img" src="' +
          img +
          '" alt="" width="80" height="80" loading="lazy">'
        : "") +
      "</a>" +
      '<div class="evj-smart-cart__meta">' +
      '<p class="evj-smart-cart__why">' +
      why +
      "</p>" +
      '<a class="evj-smart-cart__name" href="' +
      href +
      '">' +
      title +
      "</a>" +
      '<p class="evj-smart-cart__price">' +
      money(v.price) +
      "</p>" +
      "</div>" +
      btn +
      "</article>"
    );
  }

  function fetchIntent(productId, intent, limit) {
    return fetch(
      "/recommendations/products.json?product_id=" +
        encodeURIComponent(productId) +
        "&limit=" +
        encodeURIComponent(limit || 8) +
        "&intent=" +
        encodeURIComponent(intent),
      { credentials: "same-origin" }
    )
      .then(function (r) {
        return r.ok ? r.json() : { products: [] };
      })
      .then(function (data) {
        return ((data && data.products) || []).map(function (p) {
          return normalize(p, "pair");
        });
      })
      .catch(function () {
        return [];
      });
  }

  function fetchBestSellers(handle, limit) {
    if (!handle) return Promise.resolve([]);
    return fetch(
      "/collections/" +
        encodeURIComponent(handle) +
        "/products.json?limit=" +
        encodeURIComponent(limit || 8),
      { credentials: "same-origin" }
    )
      .then(function (r) {
        return r.ok ? r.json() : { products: [] };
      })
      .then(function (data) {
        return ((data && data.products) || []).map(function (p) {
          return normalize(p, "popular");
        });
      })
      .catch(function () {
        return [];
      });
  }

  function mergePriority(sameName, complementary, related, popular, cartIds, limit, titles) {
    var groups = wordGroups(titles);
    var seen = {};
    cartIds.forEach(function (id) {
      seen[String(id)] = true;
    });
    var out = [];
    function pushAll(list) {
      (list || []).forEach(function (p) {
        if (!p || !p.id || !p.available) return;
        var key = String(p.id);
        if (seen[key]) return;
        seen[key] = true;
        if (titleHasAnyWord(p.title, groups.primary)) p.source = "set";
        else if (titleHasAnyWord(p.title, groups.extra)) p.source = "pair";
        out.push(p);
      });
    }
    pushAll(sameName);
    pushAll(complementary);
    pushAll(related);
    pushAll(popular);
    out.sort(function (a, b) {
      var as = a.source === "set" ? 0 : 1;
      var bs = b.source === "set" ? 0 : 1;
      return as - bs;
    });
    return out.slice(0, limit);
  }

  var lastHtml = "";
  var lastSig = "";

  function recSignature(products, cartIds) {
    return (
      (cartIds || []).join(",") +
      "|" +
      (products || [])
        .map(function (p) {
          return p && p.id;
        })
        .join(",")
    );
  }

  function renderRoot(root, products, cartIds) {
    var list = root.querySelector(LIST_SEL);
    if (!list) return;
    var html = products.map(productCard).join("");
    if (!html) {
      if (!lastHtml) root.hidden = true;
      return;
    }
    var sig = recSignature(products, cartIds);
    if (root.getAttribute("data-evj-sig") === sig && list.innerHTML) {
      root.hidden = false;
      return;
    }
    if (list.innerHTML !== html) list.innerHTML = html;
    lastHtml = html;
    lastSig = sig;
    root.setAttribute("data-evj-sig", sig);
    root.hidden = false;
  }

  function restoreIfWiped() {
    if (!lastHtml) return false;
    var restored = false;
    document.querySelectorAll(ROOT_SEL).forEach(function (root) {
      var list = root.querySelector(LIST_SEL);
      if (!list) return;
      if (!list.innerHTML) {
        list.innerHTML = lastHtml;
        if (lastSig) root.setAttribute("data-evj-sig", lastSig);
        root.hidden = false;
        restored = true;
      }
    });
    return restored;
  }

  function addToCart(variantId, btn) {
    if (!variantId) return Promise.resolve();
    if (btn) {
      btn.disabled = true;
      btn.textContent = "Adding…";
    }
    return fetch("/cart/add.js", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    })
      .then(function (r) {
        return r.json().then(function (body) {
          return { ok: r.ok, body: body };
        });
      })
      .then(function (res) {
        if (!res.ok || res.body.status === 422) {
          throw new Error((res.body && res.body.description) || "Could not add");
        }
        document.dispatchEvent(new CustomEvent("apps:product-added-to-cart"));
        if (btn) btn.textContent = "Added";
      })
      .catch(function () {
        if (btn) {
          btn.disabled = false;
          btn.textContent = "Add";
        }
      });
  }

  function valueIsAvailable(product, index, val, selected) {
    var vs = product.variants || [];
    for (var i = 0; i < vs.length; i++) {
      var v = vs[i];
      if (v.available === false || !v.id) continue;
      if (v["option" + index] !== val) continue;
      var ok = true;
      for (var n = 1; n <= 3; n++) {
        if (n === index) continue;
        if (selected[n] && v["option" + n] !== selected[n]) ok = false;
      }
      if (ok) return true;
    }
    return false;
  }

  function selectedFromItem(item) {
    var selected = {};
    item.querySelectorAll("[data-evj-opt].is-selected").forEach(function (chip) {
      selected[Number(chip.getAttribute("data-evj-opt"))] = chip.getAttribute("data-evj-val");
    });
    return selected;
  }

  function syncPicker(item, product) {
    var opts = pickerOptions(product);
    var selected = selectedFromItem(item);
    item.querySelectorAll("[data-evj-opt]").forEach(function (chip) {
      var index = Number(chip.getAttribute("data-evj-opt"));
      var val = chip.getAttribute("data-evj-val");
      var on = valueIsAvailable(product, index, val, selected);
      chip.disabled = !on;
      chip.classList.toggle("is-unavailable", !on);
      if (!on) chip.classList.remove("is-selected");
    });
    selected = selectedFromItem(item);
    var ready = opts.every(function (o) {
      return selected[o.index];
    });
    var variant = ready ? findVariant(product, selected) : null;
    var addBtn = item.querySelector("[data-evj-confirm]");
    var priceEl = item.querySelector(".evj-smart-cart__price");
    if (variant && variant.available !== false && variant.id) {
      if (addBtn) {
        addBtn.disabled = false;
        addBtn.setAttribute("data-evj-add", variant.id);
      }
      if (priceEl) priceEl.textContent = money(variant.price);
    } else if (addBtn) {
      addBtn.disabled = true;
      addBtn.removeAttribute("data-evj-add");
    }
  }

  function openPicker(item, productId) {
    var product = productById[String(productId)];
    if (!product) return;
    if (item.querySelector(".evj-smart-cart__picker")) return;
    var opts = pickerOptions(product);
    if (!opts.length) {
      var v = firstAvailableVariant(product);
      if (v && v.id) addToCart(v.id, item.querySelector("[data-evj-choose]"));
      return;
    }
    item.classList.add("evj-smart-cart__item--picking");
    var chooseBtn = item.querySelector("[data-evj-choose]");
    if (chooseBtn) chooseBtn.hidden = true;
    var html = '<div class="evj-smart-cart__picker">';
    opts.forEach(function (o) {
      html +=
        '<p class="evj-smart-cart__picker-label">Select ' +
        o.name.toLowerCase() +
        "</p><div class=\"evj-smart-cart__chips\">";
      o.values.forEach(function (val) {
        html +=
          '<button type="button" class="evj-smart-cart__chip" data-evj-opt="' +
          o.index +
          '" data-evj-val="' +
          String(val).replace(/"/g, "&quot;") +
          '">' +
          val +
          "</button>";
      });
      html += "</div>";
    });
    html +=
      '<button type="button" class="evj-smart-cart__add btn btn--tertiary btn--x-small" data-evj-confirm disabled>Add</button></div>';
    item.insertAdjacentHTML("beforeend", html);
    syncPicker(item, product);
  }

  function loadAll() {
    var roots = document.querySelectorAll(ROOT_SEL);
    if (!roots.length) return;
    var desktopLimit = Number(roots[0].getAttribute("data-limit") || 4);
    var limit =
      window.matchMedia && window.matchMedia("(max-width: 719px)").matches
        ? 2
        : desktopLimit;
    var popularHandle = roots[0].getAttribute("data-popular-collection") || "best-sellers";
    fetchCart().then(function (cart) {
      var ids = cart.ids.length ? cart.ids : cartIdsFromRoot(roots[0]);
      var titles = cart.titles.slice();
      var seed = ids.length ? ids[ids.length - 1] : null;
      var sameP = fetchSameName(titles);
      var compP = seed ? fetchIntent(seed, "complementary", 8) : Promise.resolve([]);
      var relP = seed ? fetchIntent(seed, "related", 8) : Promise.resolve([]);
      var popP = fetchBestSellers(popularHandle, 8);

      Promise.all([sameP, compP, relP, popP]).then(function (parts) {
        var merged = mergePriority(parts[0], parts[1], parts[2], parts[3], ids, limit, titles);
        Promise.all(
          merged.map(function (p) {
            return hydrateProduct(p, p.source);
          })
        ).then(function (full) {
          full.forEach(function (p) {
            if (p && p.id) productById[String(p.id)] = p;
          });
          document.querySelectorAll(ROOT_SEL).forEach(function (root) {
            renderRoot(root, full, ids);
          });
        });
      });
    });
  }

  var loadTimer;
  function scheduleLoad() {
    restoreIfWiped();
    clearTimeout(loadTimer);
    loadTimer = setTimeout(loadAll, 400);
  }

  function captureProductView() {
    var el = document.getElementById("evj-rv-product");
    if (!el) return;
    try {
      var data = JSON.parse(el.textContent || "{}");
      recordViewed(data);
    } catch (e) {}
  }

  function onClick(e) {
    var chip = e.target.closest("[data-evj-opt]");
    if (chip) {
      e.preventDefault();
      if (chip.disabled || chip.classList.contains("is-unavailable")) return;
      var item = chip.closest(".evj-smart-cart__item");
      var product = item && productById[item.getAttribute("data-evj-product-id")];
      if (!item || !product) return;
      var index = chip.getAttribute("data-evj-opt");
      item.querySelectorAll('[data-evj-opt="' + index + '"]').forEach(function (el) {
        el.classList.toggle("is-selected", el === chip);
      });
      syncPicker(item, product);
      return;
    }
    var choose = e.target.closest("[data-evj-choose]");
    if (choose) {
      e.preventDefault();
      openPicker(choose.closest(".evj-smart-cart__item"), choose.getAttribute("data-evj-choose"));
      return;
    }
    var add = e.target.closest("[data-evj-add]");
    if (add) {
      e.preventDefault();
      addToCart(add.getAttribute("data-evj-add"), add);
    }
  }

  var addedThisOpen = false;

  function setAddedMode(on) {
    var qc = document.querySelector(".quick-cart");
    if (!qc) return;
    qc.classList.toggle("evj-cart--added", !!on);
    var heading = qc.querySelector("[data-evj-cart-heading]");
    if (heading) {
      var text = heading.firstChild;
      if (text && text.nodeType === 3) {
        text.textContent = on ? "Added to your cart" : "Cart";
      }
    }
    fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) {
        return r.ok ? r.json() : { item_count: 0 };
      })
      .then(function (cart) {
        document.querySelectorAll("[data-evj-cart-count]").forEach(function (el) {
          el.textContent = cart.item_count || 0;
        });
      })
      .catch(function () {});
  }

  document.addEventListener("click", onClick);
  document.addEventListener("apps:product-added-to-cart", function () {
    addedThisOpen = true;
    setAddedMode(true);
    scheduleLoad();
  });
  document.addEventListener("cart:item-added", function () {
    addedThisOpen = true;
    setAddedMode(true);
    scheduleLoad();
  });
  document.addEventListener("quick-cart:open", function () {
    if (addedThisOpen) {
      setAddedMode(true);
      addedThisOpen = false;
    } else {
      setAddedMode(false);
    }
  });
  document.addEventListener("cart:updated", scheduleLoad);
  document.addEventListener("quick-cart:updated", scheduleLoad);
  document.addEventListener("shopify:section:load", scheduleLoad);

  if (window.MutationObserver) {
    var mo = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var nodes = records[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var n = nodes[j];
          if (n.nodeType !== 1) continue;
          if ((n.matches && n.matches(ROOT_SEL)) || (n.querySelector && n.querySelector(ROOT_SEL))) {
            restoreIfWiped();
            var empty = document.querySelector(ROOT_SEL + " " + LIST_SEL + ":empty");
            if (empty) scheduleLoad();
            return;
          }
        }
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  function boot() {
    captureProductView();
    loadAll();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
